// Fix IJ (2026-10-30): "Acá agregar «Curva S» (financiera presupuesto) ... la curva debe generarse a partir de los
// tiempos de ejecución de las tareas y los puntos de las curvas deben ser por mes. Eje Y: monto de presupuesto en Gs,
// eje X: tiempo de obra en meses. Ej. de visualización: barra + curva" (CEN). Cada tarea reparte su precio de venta
// (el de la vista cliente, Fix IF) entre los meses de su período, en proporción a los días de cada mes.
import { useMemo } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { formatPYG } from "../../utils/format";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIA = 86400000;
const aDia = (s) => { const [y, m, d] = String(s).slice(0, 10).split("-").map(Number); return Date.UTC(y, m - 1, d); };
const millones = (v) => `${Math.round((Number(v) || 0) / 1e6).toLocaleString("es-PY")} M`;

// Período de la tarea: el calendarizado (plan); si no tiene, el del presupuesto.
export function periodoTarea(t) {
  const ok = (r) => r && r.inicio && r.fin && aDia(r.fin) >= aDia(r.inicio);
  if (ok(t.plan)) return { inicio: t.plan.inicio, fin: t.plan.fin, base: false };
  if (ok(t.plan_presupuesto)) return { inicio: t.plan_presupuesto.inicio, fin: t.plan_presupuesto.fin, base: true };
  return null;
}

export function curvaPorMes(tareas) {
  const porMes = {};
  let sinFechas = 0, montoSinFechas = 0, conBase = 0;
  (tareas || []).forEach(t => {
    const monto = Number(t.precio_venta) || 0;
    if (monto <= 0) return;
    const p = periodoTarea(t);
    if (!p) { sinFechas += 1; montoSinFechas += monto; return; }
    if (p.base) conBase += 1;
    const ini = aDia(p.inicio), fin = aDia(p.fin);
    const dias = Math.round((fin - ini) / DIA) + 1;
    for (let d = ini; d <= fin; d += DIA) {
      const f = new Date(d);
      const k = `${f.getUTCFullYear()}-${String(f.getUTCMonth() + 1).padStart(2, "0")}`;
      porMes[k] = (porMes[k] || 0) + monto / dias;
    }
  });
  const claves = Object.keys(porMes).sort();
  if (!claves.length) return { meses: [], total: 0, sinFechas, montoSinFechas, conBase };
  const [y0, m0] = claves[0].split("-").map(Number), [y1, m1] = claves[claves.length - 1].split("-").map(Number);
  const meses = [];
  let acum = 0;
  for (let y = y0, m = m0; y < y1 || (y === y1 && m <= m1); m === 12 ? (y += 1, m = 1) : (m += 1)) {
    const k = `${y}-${String(m).padStart(2, "0")}`;
    const mensual = Math.round(porMes[k] || 0);
    acum += mensual;
    meses.push({ clave: k, mes: `${MESES[m - 1]} ${y}`, corto: `${MESES[m - 1].slice(0, 3)} ${String(y).slice(2)}`, mensual, acumulado: acum });
  }
  return { meses, total: acum, sinFechas, montoSinFechas, conBase };
}

export default function CurvaS({ tareas }) {
  const c = useMemo(() => curvaPorMes(tareas), [tareas]);
  if (!c.meses.length) return <p className="text-xs text-gray-400 italic py-4 text-center" data-testid="curva-s-vacia">No hay tareas con fechas y precio de venta para armar la curva.</p>;
  return (
    <div className="space-y-3" data-testid="curva-s">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-bold text-gray-800">Curva S — precio de venta por mes</p>
        <p className="text-xs text-gray-500">Total calendarizado: <strong className="text-gray-800" data-testid="curva-s-total">{formatPYG(c.total)}</strong></p>
      </div>
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={c.meses} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="corto" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="mes" tickFormatter={millones} tick={{ fontSize: 11 }} width={62} />
            <YAxis yAxisId="acum" orientation="right" tickFormatter={millones} tick={{ fontSize: 11 }} width={62} />
            <Tooltip formatter={(v, n) => [formatPYG(v), n]} labelFormatter={(l, p) => (p && p[0] ? p[0].payload.mes : l)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="mes" dataKey="mensual" name="Del mes" fill="#2E6DA4" radius={[3, 3, 0, 0]} />
            <Line yAxisId="acum" type="monotone" dataKey="acumulado" name="Acumulado" stroke="#EA580C" strokeWidth={2.5} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="curva-s-tabla">
          <thead><tr className="text-gray-400 uppercase text-[10px] border-b border-gray-100">
            <th className="text-left py-1.5 pr-3">Mes</th><th className="text-right py-1.5 pr-3">Del mes</th>
            <th className="text-right py-1.5 pr-3">Acumulado</th><th className="text-right py-1.5">% acumulado</th></tr></thead>
          <tbody className="divide-y divide-gray-50">
            {c.meses.map(m => (
              <tr key={m.clave} data-testid={`curva-s-mes-${m.clave}`}>
                <td className="py-1.5 pr-3 text-gray-700">{m.mes}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{formatPYG(m.mensual)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums font-semibold">{formatPYG(m.acumulado)}</td>
                <td className="py-1.5 text-right tabular-nums">{c.total ? Math.round((m.acumulado / c.total) * 1000) / 10 : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(c.sinFechas > 0 || c.conBase > 0) && (
        <p className="text-[11px] text-gray-500" data-testid="curva-s-notas">
          {c.conBase > 0 && <>{c.conBase} tarea(s) sin calendarizar usan el plazo según presupuesto. </>}
          {c.sinFechas > 0 && <>{c.sinFechas} tarea(s) sin fechas ({formatPYG(c.montoSinFechas)}) no entran en la curva.</>}
        </p>
      )}
    </div>
  );
}
